import { useCallback, useMemo } from "react";
import Supercluster from "supercluster";
import { clamp } from "@/utilities/clamp";

type Coord = [number, number, string, boolean];

// ─── Types ────────────────────────────────────────────────────────

interface VehicleProperties {
  deviceId: string;
  isDriving: boolean;
  lat: number;
  lng: number;
}

export type PointFeature = Supercluster.PointFeature<VehicleProperties>;
export type ClusterFeature = Supercluster.ClusterFeature<Record<string, never>>;

export type ClusterOrPoint = PointFeature | ClusterFeature;

export function isCluster(f: ClusterOrPoint): f is ClusterFeature {
  return "cluster" in f.properties && f.properties.cluster === true;
}

export interface ViewportBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

const WORLD_BOUNDS: [number, number, number, number] = [-180, -85, 180, 85];
const MIN_LAT = -85;
const MAX_LAT = 85;
const MIN_ZOOM = 0;
const MAX_ZOOM = 20;
const DEFAULT_RADIUS = 64;
const MIN_RADIUS = 1;
const MAX_RADIUS = 256;

export interface ClusterOptions {
  /** Pass `false` to draw every vehicle on its own. */
  enabled?: boolean;
  /** How close together, in pixels, vehicles have to be to be folded into one bubble. */
  radius?: number;
}

/**
 * Deterministic world clustering backed by supercluster.
 *
 * @param positions - Array of [lng, lat, deviceId, isDriving] tuples
 * @param zoom - Current fractional zoom level
 * @param options - How to cluster; left out, vehicles are grouped as they always were.
 */
export function useCluster(positions: Coord[], zoom: number, options?: ClusterOptions) {
  const enabled = options?.enabled ?? true;
  // Supercluster's own limit. A radius outside it makes it throw rather than clamp.
  const radius = clamp(options?.radius ?? DEFAULT_RADIUS, MIN_RADIUS, MAX_RADIUS);
  // Use floor so cluster levels only change when actually crossing whole zoom steps.
  const zoomLevel = clampZoom(Math.floor(zoom));

  const points = useMemo(() => {
    const dedupedByDevice = new globalThis.Map<string, Coord>();

    for (const coord of positions) {
      dedupedByDevice.set(coord[2], coord);
    }

    return Array.from(dedupedByDevice.values())
      .sort((a, b) => a[2].localeCompare(b[2]))
      .flatMap(([lng, lat, deviceId, isDriving]): PointFeature[] => {
        const safeLng = normalizeLng(lng);
        const safeLat = clamp(lat, MIN_LAT, MAX_LAT);

        if (!Number.isFinite(safeLng) || !Number.isFinite(safeLat)) return [];

        return [
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: [safeLng, safeLat] },
            properties: {
              deviceId,
              isDriving,
              lat: safeLat,
              lng: safeLng,
            },
          },
        ];
      });
  }, [positions]);

  const clusterIndex = useMemo(() => {
    // Not built at all while clustering is off: indexing a whole fleet is work for an answer
    // nobody asks for.
    if (!enabled) {
      return null;
    }

    const index = new Supercluster<VehicleProperties, Record<string, never>>({
      minZoom: MIN_ZOOM,
      maxZoom: 18,
      radius,
      extent: 512,
      nodeSize: 64,
      minPoints: 2,
    });
    index.load(points);
    return index;
  }, [enabled, points, radius]);

  const clustered = useMemo(
    () =>
      // With clustering off every vehicle is its own point, which is what the index would hand
      // back anyway once nothing may be folded together.
      clusterIndex
        ? (clusterIndex.getClusters(WORLD_BOUNDS, zoomLevel) as ClusterOrPoint[])
        : (points as ClusterOrPoint[]),
    [clusterIndex, zoomLevel, points],
  );

  const getClusterExpansionZoom = useCallback(
    (clusterId: number) => {
      try {
        // Only reachable by clicking a bubble, and there are none without an index.
        return clampZoom(clusterIndex?.getClusterExpansionZoom(clusterId) ?? zoomLevel + 2);
      } catch {
        return clampZoom(zoomLevel + 2);
      }
    },
    [clusterIndex, zoomLevel],
  );

  return { clusters: clustered, getClusterExpansionZoom };
}

function normalizeLng(lng: number): number {
  if (!Number.isFinite(lng)) return Number.NaN;
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

function clampZoom(zoom: number): number {
  return clamp(zoom, MIN_ZOOM, MAX_ZOOM);
}
